import { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import styles from './ArticleEditor.module.scss';

interface ArticleEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function ArticleEditor({ value, onChange, placeholder = 'Write your article in Markdown...' }: ArticleEditorProps) {
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  }, [onChange]);

  return (
    <div className={styles.editor}>
      <div className={styles.editor__tabs}>
        <button
          type="button"
          className={`${styles.editor__tab} ${activeTab === 'write' ? styles['editor__tab--active'] : ''}`}
          onClick={() => setActiveTab('write')}
        >
          Write
        </button>
        <button
          type="button"
          className={`${styles.editor__tab} ${activeTab === 'preview' ? styles['editor__tab--active'] : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
      </div>

      <div className={styles.editor__content}>
        {activeTab === 'write' ? (
          <textarea
            className={styles.editor__textarea}
            value={value}
            onChange={handleTextChange}
            placeholder={placeholder}
          />
        ) : (
          <div className={styles.editor__preview}>
            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <p className={styles.editor__emptyPreview}>Nothing to preview yet</p>
            )}
          </div>
        )}
      </div>

      <div className={styles.editor__help}>
        <span>Supports Markdown, code blocks, and LaTeX math ($...$ or $$...$$)</span>
      </div>
    </div>
  );
}
